import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  UserNotificationPreference,
  NotificationType,
  DigestFrequency,
  NotificationPreferenceUpdate,
  UpdateNotificationPreferencesRequest
} from '../../models/file-vault.models';

interface PreferenceRow {
  eventType: string;
  label: string;
  description: string;
  emailEnabled: boolean;
  inAppEnabled: boolean;
  digestFrequency: DigestFrequency;
  isModified: boolean;
}

@Component({
  selector: 'fv-notification-preferences',
  standalone: true,
  template: `
    <div class="notification-preferences">
      <!-- Header -->
      <div class="prefs-header">
        <h3 class="prefs-title">Notification Preferences</h3>
        <p class="prefs-description">Configure how and when you receive notifications.</p>
      </div>

      <!-- Loading state -->
      <div class="loading-state" *ngIf="isLoading">
        <span class="spinner"></span>
        <span>Loading preferences...</span>
      </div>

      <!-- Error state -->
      <div class="error-state" *ngIf="error">
        <span class="error-icon">⚠️</span>
        <span>{{ error }}</span>
        <button class="retry-btn" (click)="loadPreferences()">Retry</button>
      </div>

      <!-- Preferences form -->
      <div class="prefs-content" *ngIf="!isLoading && !error">
        <!-- Global settings -->
        <div class="section">
          <h4 class="section-title">Global Settings</h4>
          <div class="global-settings">
            <div class="setting-row">
              <div class="setting-info">
                <span class="setting-label">Default Digest Frequency</span>
                <span class="setting-desc">How often to receive email summaries</span>
              </div>
              <select [(ngModel)]="globalDigestFrequency" (ngModelChange)="onGlobalDigestChange()" class="select-control">
                <option *ngFor="let freq of digestOptions" [ngValue]="freq.value">{{ freq.label }}</option>
              </select>
            </div>
            <div class="setting-row" *ngIf="showQuietHours">
              <div class="setting-info">
                <span class="setting-label">Quiet Hours</span>
                <span class="setting-desc">Pause notifications during these hours</span>
              </div>
              <div class="quiet-hours-controls">
                <input type="time" [(ngModel)]="quietHoursStart" class="time-input" placeholder="Start">
                <span class="to-label">to</span>
                <input type="time" [(ngModel)]="quietHoursEnd" class="time-input" placeholder="End">
              </div>
            </div>
          </div>
        </div>

        <!-- Per-event settings -->
        <div class="section">
          <h4 class="section-title">Event Notifications</h4>
          <p class="section-desc">Choose how you want to be notified for each event type.</p>

          <div class="prefs-table">
            <div class="table-header">
              <span class="col-event">Event Type</span>
              <span class="col-email">Email</span>
              <span class="col-inapp">In-App</span>
              <span class="col-digest">Digest</span>
            </div>

            <div class="table-body">
              <div
                *ngFor="let pref of preferences; trackBy: trackByEventType"
                class="pref-row"
                [class.modified]="pref.isModified">

                <div class="col-event">
                  <span class="event-icon">{{ getEventIcon(pref.eventType) }}</span>
                  <div class="event-info">
                    <span class="event-label">{{ pref.label }}</span>
                    <span class="event-desc">{{ pref.description }}</span>
                  </div>
                </div>

                <div class="col-email">
                  <label class="toggle">
                    <input
                      type="checkbox"
                      [(ngModel)]="pref.emailEnabled"
                      (ngModelChange)="onPrefChange(pref)">
                    <span class="toggle-slider"></span>
                  </label>
                </div>

                <div class="col-inapp">
                  <label class="toggle">
                    <input
                      type="checkbox"
                      [(ngModel)]="pref.inAppEnabled"
                      (ngModelChange)="onPrefChange(pref)">
                    <span class="toggle-slider"></span>
                  </label>
                </div>

                <div class="col-digest">
                  <select
                    [(ngModel)]="pref.digestFrequency"
                    (ngModelChange)="onPrefChange(pref)"
                    class="digest-select"
                    [disabled]="!pref.emailEnabled">
                    <option *ngFor="let freq of digestOptions" [ngValue]="freq.value">{{ freq.label }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="prefs-actions">
          <button class="btn btn-secondary" (click)="resetToDefaults()" [disabled]="isSaving">
            Reset to Defaults
          </button>
          <div class="action-group">
            <span class="unsaved-indicator" *ngIf="hasUnsavedChanges">Unsaved changes</span>
            <button class="btn btn-primary" (click)="savePreferences()" [disabled]="!hasUnsavedChanges || isSaving">
              {{ isSaving ? 'Saving...' : 'Save Preferences' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Success message -->
      <div class="success-toast" *ngIf="showSuccess">
        ✓ Preferences saved successfully
      </div>
    </div>
  `,
  styles: [`
    .notification-preferences {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
    }

    .prefs-header {
      margin-bottom: 24px;
    }

    .prefs-title {
      font-size: 20px;
      font-weight: 600;
      color: var(--fv-text);
      margin: 0 0 8px 0;
    }

    .prefs-description {
      font-size: 14px;
      color: var(--fv-text-secondary);
      margin: 0;
    }

    .loading-state, .error-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      color: var(--fv-text-secondary);
      gap: 12px;
    }

    .spinner {
      width: 24px;
      height: 24px;
      border: 2px solid var(--fv-border);
      border-top-color: var(--fv-text-accent);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .error-icon {
      font-size: 32px;
    }

    .retry-btn {
      padding: 8px 16px;
      background: #1976d2;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    }

    .section {
      margin-bottom: 32px;
    }

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: var(--fv-text);
      margin: 0 0 8px 0;
    }

    .section-desc {
      font-size: 13px;
      color: var(--fv-text-secondary);
      margin: 0 0 16px 0;
    }

    .global-settings {
      background: var(--fv-surface-alt);
      border-radius: 8px;
      padding: 16px;
    }

    .setting-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 0;
    }

    .setting-row + .setting-row {
      border-top: 1px solid var(--fv-border);
    }

    .setting-info {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .setting-label {
      font-size: 14px;
      font-weight: 500;
      color: var(--fv-text);
    }

    .setting-desc {
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .select-control {
      padding: 8px 12px;
      font-size: 14px;
      border: 1px solid var(--fv-border);
      border-radius: 4px;
      background: var(--fv-surface);
      color: var(--fv-text);
      min-width: 150px;
    }

    .quiet-hours-controls {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .time-input {
      padding: 8px;
      font-size: 14px;
      border: 1px solid var(--fv-border);
      border-radius: 4px;
      width: 110px;
      background: var(--fv-surface);
      color: var(--fv-text);
    }

    .to-label {
      color: var(--fv-text-secondary);
      font-size: 13px;
    }

    .prefs-table {
      border: 1px solid var(--fv-border);
      border-radius: 8px;
      overflow: hidden;
    }

    .table-header {
      display: grid;
      grid-template-columns: 1fr 80px 80px 130px;
      gap: 16px;
      padding: 12px 16px;
      background: var(--fv-surface-alt);
      border-bottom: 1px solid var(--fv-border);
      font-size: 12px;
      font-weight: 600;
      color: var(--fv-text-secondary);
      text-transform: uppercase;
    }

    .table-body {
      max-height: 400px;
      overflow-y: auto;
    }

    .pref-row {
      display: grid;
      grid-template-columns: 1fr 80px 80px 130px;
      gap: 16px;
      padding: 12px 16px;
      align-items: center;
      border-bottom: 1px solid var(--fv-border-subtle);
      transition: background 0.2s;
    }

    .pref-row:last-child {
      border-bottom: none;
    }

    .pref-row:hover {
      background: var(--fv-surface-alt);
    }

    .pref-row.modified {
      background: var(--fv-status-warning-bg);
    }

    .col-event {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .event-icon {
      font-size: 20px;
    }

    .event-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .event-label {
      font-size: 14px;
      font-weight: 500;
      color: var(--fv-text);
    }

    .event-desc {
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .col-email, .col-inapp {
      display: flex;
      justify-content: center;
    }

    .col-digest {
      display: flex;
      justify-content: center;
    }

    .toggle {
      position: relative;
      display: inline-block;
      width: 44px;
      height: 24px;
      cursor: pointer;
    }

    .toggle input {
      opacity: 0;
      width: 0;
      height: 0;
    }

    .toggle-slider {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--fv-text-muted);
      border-radius: 24px;
      transition: 0.3s;
    }

    .toggle-slider:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: var(--fv-surface);
      border-radius: 50%;
      transition: 0.3s;
    }

    .toggle input:checked + .toggle-slider {
      background-color: var(--fv-text-accent);
    }

    .toggle input:checked + .toggle-slider:before {
      transform: translateX(20px);
    }

    .digest-select {
      padding: 6px 8px;
      font-size: 12px;
      border: 1px solid var(--fv-border);
      border-radius: 4px;
      background: var(--fv-surface);
      color: var(--fv-text);
      width: 100%;
    }

    .digest-select:disabled {
      background: var(--fv-surface-hover);
      color: var(--fv-text-muted);
    }

    .prefs-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 24px;
      border-top: 1px solid var(--fv-border);
    }

    .action-group {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .unsaved-indicator {
      font-size: 13px;
      color: var(--fv-status-warning-fg);
    }

    .btn {
      padding: 10px 20px;
      font-size: 14px;
      font-weight: 500;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      transition: background 0.2s;
    }

    .btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .btn-primary {
      background: var(--fv-text-accent);
      color: white;
    }

    .btn-primary:hover:not(:disabled) {
      background: var(--fv-text-accent);
      filter: brightness(0.9);
    }

    .btn-secondary {
      background: var(--fv-surface-hover);
      color: var(--fv-text-secondary);
      border: 1px solid var(--fv-border);
    }

    .btn-secondary:hover:not(:disabled) {
      background: var(--fv-border-subtle);
    }

    .success-toast {
      position: fixed;
      bottom: 24px;
      right: 24px;
      padding: 12px 24px;
      background: #43a047;
      color: white;
      border-radius: 4px;
      box-shadow: 0 2px 8px var(--fv-shadow);
      animation: slideIn 0.3s ease-out;
    }

    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
  `],
  imports: [CommonModule, FormsModule]
})
export class NotificationPreferencesComponent implements OnInit {
  private api = inject(FileVaultApiService);

  /** Show quiet hours setting */
  @Input() showQuietHours = true;

  /** Event when preferences are saved */
  @Output() saved = new EventEmitter<void>();

  /** Event when cancel/close is requested */
  @Output() cancel = new EventEmitter<void>();

  preferences: PreferenceRow[] = [];
  originalPreferences: PreferenceRow[] = [];
  isLoading = false;
  isSaving = false;
  error: string | null = null;
  showSuccess = false;

  globalDigestFrequency = DigestFrequency.Immediate;
  quietHoursStart = '';
  quietHoursEnd = '';

  digestOptions = [
    { value: DigestFrequency.Immediate, label: 'Immediate' },
    { value: DigestFrequency.Hourly, label: 'Hourly' },
    { value: DigestFrequency.Daily, label: 'Daily' },
    { value: DigestFrequency.Weekly, label: 'Weekly' },
    { value: DigestFrequency.None, label: 'Never' }
  ];

  private eventTypeConfig: Record<string, { label: string; description: string; icon: string }> = {
    '*': { label: 'All Events', description: 'Default setting for all notification types', icon: '🔔' },
    'WorkflowAssigned': { label: 'Workflow Assigned', description: 'When a workflow task is assigned to you', icon: '📋' },
    'ApprovalRequired': { label: 'Approval Required', description: 'When your approval is needed', icon: '✋' },
    'StateChanged': { label: 'State Changed', description: 'When an item\'s lifecycle state changes', icon: '🔄' },
    'TaskOverdue': { label: 'Task Overdue', description: 'When a task passes its due date', icon: '⏰' },
    'ChangeSubmitted': { label: 'Change Submitted', description: 'When a change request is submitted', icon: '📝' },
    'ChangeApproved': { label: 'Change Approved', description: 'When a change is approved', icon: '✅' },
    'ChangeRejected': { label: 'Change Rejected', description: 'When a change is rejected', icon: '❌' },
    'FileCheckedOut': { label: 'File Checked Out', description: 'When a file you\'re interested in is checked out', icon: '📤' },
    'FileCheckedIn': { label: 'File Checked In', description: 'When a file you\'re interested in is checked in', icon: '📥' },
    'Mention': { label: 'Mentions', description: 'When someone mentions you in a comment', icon: '💬' },
    'PermissionGranted': { label: 'Permission Granted', description: 'When you\'re granted new permissions', icon: '🔓' },
    'PermissionRevoked': { label: 'Permission Revoked', description: 'When a permission is revoked', icon: '🔒' },
    'SystemAnnouncement': { label: 'System Announcements', description: 'System-wide announcements and updates', icon: '📢' }
  };

  get hasUnsavedChanges(): boolean {
    return this.preferences.some(p => p.isModified);
  }

  ngOnInit(): void {
    this.loadPreferences();
  }

  async loadPreferences(): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const prefs = await this.api.getNotificationPreferences().toPromise();
      this.mapPreferencesToRows(prefs ?? []);
    } catch (err) {
      this.error = 'Failed to load notification preferences. Please try again.';
      console.error('Failed to load preferences', err);
    } finally {
      this.isLoading = false;
    }
  }

  private mapPreferencesToRows(prefs: UserNotificationPreference[]): void {
    // Create a map of existing preferences
    const prefsMap = new Map<string, UserNotificationPreference>();
    prefs.forEach(p => prefsMap.set(p.eventType, p));

    // Get global preference for default values
    const globalPref = prefsMap.get('*');
    if (globalPref) {
      this.globalDigestFrequency = globalPref.digestFrequency;
      this.quietHoursStart = globalPref.quietHoursStart ?? '';
      this.quietHoursEnd = globalPref.quietHoursEnd ?? '';
    }

    // Build preference rows for all event types
    this.preferences = Object.entries(this.eventTypeConfig).map(([eventType, config]) => {
      const existingPref = prefsMap.get(eventType);
      return {
        eventType,
        label: config.label,
        description: config.description,
        emailEnabled: existingPref?.emailEnabled ?? true,
        inAppEnabled: existingPref?.inAppEnabled ?? true,
        digestFrequency: existingPref?.digestFrequency ?? DigestFrequency.Immediate,
        isModified: false
      };
    });

    // Store original for comparison
    this.originalPreferences = this.preferences.map(p => ({ ...p }));
  }

  onPrefChange(pref: PreferenceRow): void {
    const original = this.originalPreferences.find(p => p.eventType === pref.eventType);
    if (original) {
      pref.isModified = (
        pref.emailEnabled !== original.emailEnabled ||
        pref.inAppEnabled !== original.inAppEnabled ||
        pref.digestFrequency !== original.digestFrequency
      );
    }
  }

  onGlobalDigestChange(): void {
    // Update all preferences that are set to use the global default
    const globalPref = this.preferences.find(p => p.eventType === '*');
    if (globalPref) {
      globalPref.digestFrequency = this.globalDigestFrequency;
      this.onPrefChange(globalPref);
    }
  }

  async savePreferences(): Promise<void> {
    this.isSaving = true;

    try {
      const updates: NotificationPreferenceUpdate[] = this.preferences
        .filter(p => p.isModified)
        .map(p => ({
          eventType: p.eventType,
          emailEnabled: p.emailEnabled,
          inAppEnabled: p.inAppEnabled,
          digestFrequency: p.digestFrequency
        }));

      const request: UpdateNotificationPreferencesRequest = { preferences: updates };
      await this.api.updateNotificationPreferences(request).toPromise();

      // Update original preferences
      this.originalPreferences = this.preferences.map(p => ({ ...p, isModified: false }));
      this.preferences.forEach(p => p.isModified = false);

      // Show success message
      this.showSuccess = true;
      setTimeout(() => this.showSuccess = false, 3000);

      this.saved.emit();
    } catch (err) {
      console.error('Failed to save preferences', err);
      this.error = 'Failed to save preferences. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  resetToDefaults(): void {
    this.preferences.forEach(pref => {
      pref.emailEnabled = true;
      pref.inAppEnabled = true;
      pref.digestFrequency = DigestFrequency.Immediate;
      this.onPrefChange(pref);
    });
    this.globalDigestFrequency = DigestFrequency.Immediate;
    this.quietHoursStart = '';
    this.quietHoursEnd = '';
  }

  getEventIcon(eventType: string): string {
    return this.eventTypeConfig[eventType]?.icon ?? '📌';
  }

  trackByEventType(index: number, pref: PreferenceRow): string {
    return pref.eventType;
  }
}
